/**
 * @fileoverview Collapse document lines left blank by an empty field.
 *
 * Purpose:
 * - Address blocks are authored as a fixed stack of lines — name, address
 *   line 1, address line 2, then suburb/state/postcode. When a contact has
 *   no second address line, substituting an empty value used to leave the
 *   line behind as a blank row, so the suburb line sat one line too low.
 * - The same gap shows up on letterhead stacks (company name / additional
 *   trading-name lines / ABN / ACN): an empty `company_name_variant_lines`
 *   field sits on its own centred paragraph (or table row), and without
 *   collapsing it the ABN line sits one row too low.
 *
 * How it works:
 * - The renderer emits {@link EMPTY_FIELD_MARKER} (an HTML comment) in place
 *   of a field whose value resolved to empty, instead of an empty string.
 * - `collapseEmptyFieldLines` then removes any line whose ONLY content came
 *   from such fields, and strips the remaining markers.
 *
 * Paragraphs and headings are collapsed first so a wrapping `<div>` or
 * table cell cannot swallow them (the previous single-regex pass matched
 * the outer block and left an empty `<p>` behind, which
 * `preserveEmptyParagraphs` then inflated into a visible blank line).
 * A one-cell table row whose only content was an empty field is dropped
 * so centred Word-style heading tables close the same gap; a row that
 * still has another cell is kept (emptying one cell must not shift
 * columns).
 *
 * The marker is what makes this safe: a blank line the author added on
 * purpose carries no marker, so it survives (see `preserveEmptyParagraphs`
 * in `src/utils/export/exportHtmlDocument.ts`, which deliberately keeps
 * those). A line that still has literal text ("Attention:", a comma) or
 * another field's value also survives — only fully-empty lines go.
 *
 * Used by:
 * - `replaceFieldPlaceholders` in the day-one route, which feeds the
 *   preview, the PDF download, and the Word download.
 */

/**
 * Emitted in place of a field whose value resolved to empty, so the line it
 * sat on can be identified and collapsed. An HTML comment because it must
 * survive the intermediate string transforms untouched and render as
 * nothing if it somehow escapes the collapse pass.
 */
export const EMPTY_FIELD_MARKER = "<!--morgan-empty-field-->";

/** Any hard-break form the editors emit: `<br>`, `<br />`, `<br class=…>`. */
const BR_RE = /<br\b[^>]*>/i;

/** Blocks we empty rather than delete, because the structure carries meaning. */
const STRUCTURAL_TAGS = new Set(["td", "th"]);

/**
 * Void / embedded content that is invisible to a text-only check but must
 * never be dropped (a signature image, a divider, a nested table).
 */
const NON_TEXT_CONTENT_RE = /<(?:img|hr|table|svg|iframe|input|object)\b/i;

/**
 * Innermost paragraphs / headings. Matched before wrapping `<div>` / `<td>`
 * so a centred letterhead stack (one field per `<p>`) still collapses.
 */
const PARAGRAPH_RE = /<(p|h[1-6])\b([^>]*)>((?:(?!<\1\b)[\s\S])*?)<\/\1>/gi;

const CELL_RE = /<(td|th)\b([^>]*)>((?:(?!<\1\b)[\s\S])*?)<\/\1>/gi;

/** Innermost divs only, so collection repeat wrappers keep their children. */
const DIV_RE = /<div\b([^>]*)>((?:(?!<div\b)[\s\S])*?)<\/div>/gi;

const ROW_RE = /<tr\b([^>]*)>((?:(?!<tr\b)[\s\S])*?)<\/tr>/gi;

const CELL_INNER_RE = /<(td|th)\b([^>]*)>((?:(?!<\1\b)[\s\S])*?)<\/\1>/gi;

/** Visible text of a fragment, ignoring tags, entities, and our markers. */
function visibleText(fragment: string): string {
  return fragment
    .split(EMPTY_FIELD_MARKER)
    .join("")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
}

/** Did this line lose all of its content to empty fields? */
function isEmptiedLine(line: string): boolean {
  if (!line.includes(EMPTY_FIELD_MARKER)) return false;
  if (NON_TEXT_CONTENT_RE.test(line)) return false;
  return visibleText(line) === "";
}

/**
 * True when a resolved field value has nothing a reader would see — empty,
 * whitespace, a bare `<br>`, or a stack-layout leftover of `"\n"`. Images
 * and other embedded content still count as present.
 *
 * Used by `replaceFieldPlaceholders` so those values emit a marker (and
 * collapse) instead of painting a blank line.
 */
export function isResolvedFieldEmpty(
  resolved: string | null | undefined,
): boolean {
  if (resolved == null) return true;
  if (NON_TEXT_CONTENT_RE.test(resolved)) return false;
  return visibleText(resolved) === "";
}

function isVisuallyBlank(line: string): boolean {
  if (NON_TEXT_CONTENT_RE.test(line)) return false;
  return visibleText(line) === "";
}

/**
 * Drop blank fragments that a trailing `<br>` leaves at the edges of a
 * block after the emptied field line itself has been removed.
 */
function trimEdgeBlank(lines: string[]): string[] {
  const start = lines.findIndex((line) => !isVisuallyBlank(line ?? ""));
  if (start === -1) return [];
  const fromEnd = [...lines]
    .reverse()
    .findIndex((line) => !isVisuallyBlank(line ?? ""));
  return lines.slice(start, lines.length - fromEnd);
}

function keptLinesAfterCollapse(inner: string): string[] | null {
  if (!inner.includes(EMPTY_FIELD_MARKER)) return null;
  const lines = inner.split(BR_RE);
  const withoutEmptied = lines.filter((line) => !isEmptiedLine(line));
  if (withoutEmptied.length === lines.length) return null;
  return trimEdgeBlank(withoutEmptied);
}

function rewriteBlock(
  tag: string,
  attrs: string,
  inner: string,
): string | null {
  const kept = keptLinesAfterCollapse(inner);
  if (kept === null) return null;

  const tagName = tag.toLowerCase();
  if (kept.length === 0) {
    // Nothing left to render. Table cells stay (removing one would shift
    // every following cell); a paragraph, heading, or wrapping div goes
    // entirely, which is what closes the gap when a whole line was one field.
    return STRUCTURAL_TAGS.has(tagName) ? `<${tag}${attrs}></${tag}>` : "";
  }
  return `<${tag}${attrs}>${kept.join("<br>")}</${tag}>`;
}

function collapseMatchingBlocks(
  html: string,
  regex: RegExp,
  parts: (captures: string[]) => { tag: string; attrs: string; inner: string },
): string {
  return html.replace(regex, (full, ...captures: string[]) => {
    const { tag, attrs, inner } = parts(captures);
    if (!inner.includes(EMPTY_FIELD_MARKER)) return full;
    return rewriteBlock(tag, attrs, inner) ?? full;
  });
}

/**
 * A zero bottom-margin declaration on a block's inline `style`, in any unit
 * (or unitless) with optional `!important` and trailing semicolon. Used to
 * detect a paragraph that deliberately butts against the block below it.
 *
 * The leading `(^|[;"'\s])` capture is a declaration boundary so longhand
 * properties that merely *end* in `margin-bottom` — `scroll-margin-bottom`,
 * `-webkit-margin-bottom` — are never matched. Quotes are boundaries too
 * because the match is also run against a whole opening tag, where the
 * declaration can follow the `style` attribute's opening quote directly. The
 * boundary character is captured so {@link restoreBottomMargin} can put it
 * back when it strips the declaration, rather than fusing the neighbouring
 * declarations together.
 */
const ZERO_BOTTOM_MARGIN_RE =
  /(^|[;"'\s])margin-bottom\s*:\s*0(?:\.0+)?(?:pt|px|em|rem|%|cm|mm|in)?\s*(?:!important)?\s*;?/i;

/** Opening tag of a paragraph / heading, capturing its attributes. */
const PARAGRAPH_OPEN_RE = /^<(p|h[1-6])\b([^>]*)>/i;

/** True when a paragraph's inner content would fully collapse to nothing. */
function isFieldOnlyEmptyParagraph(inner: string): boolean {
  if (!inner.includes(EMPTY_FIELD_MARKER)) return false;
  if (NON_TEXT_CONTENT_RE.test(inner)) return false;
  return visibleText(inner) === "";
}

/**
 * Remove a `margin-bottom: 0` override from a paragraph / heading's opening
 * tag so the block's normal (theme) bottom margin applies again. Drops the
 * whole `style` attribute if nothing else remains. Blocks without such an
 * override are returned unchanged.
 */
function restoreBottomMargin(block: string): string {
  return block.replace(PARAGRAPH_OPEN_RE, (openTag, tag: string, attrs) => {
    const attrStr = typeof attrs === "string" ? attrs : "";
    const styleMatch = /\sstyle=("|')([\s\S]*?)\1/i.exec(attrStr);
    if (!styleMatch) return openTag;
    const styleBody = styleMatch[2] ?? "";
    if (!ZERO_BOTTOM_MARGIN_RE.test(styleBody)) return openTag;
    const nextStyle = styleBody.replace(ZERO_BOTTOM_MARGIN_RE, "$1").trim();
    const nextAttrs =
      nextStyle.replace(/^;+|;+$/g, "").trim() === ""
        ? attrStr.replace(styleMatch[0], "")
        : attrStr.replace(
            styleMatch[0],
            ` style=${styleMatch[1]}${nextStyle}${styleMatch[1]}`,
          );
    return `<${tag}${nextAttrs.replace(/\s+$/, "")}>`;
  });
}

/**
 * Preserve the visual gap a soon-to-be-collapsed field paragraph provided.
 *
 * When a paragraph / heading with an explicit `margin-bottom: 0` is
 * immediately followed by a paragraph / heading whose only content is an
 * empty field (so the collapse pass will delete it entirely), the zeroed
 * block was relying on that now-removed paragraph for its spacing — e.g. a
 * centred company-name block that sits directly above a Trust ABN line. Once
 * the empty paragraph is gone, the zero margin would butt the block against
 * the following body text. Strip the zero-margin override so the block's
 * normal bottom margin restores the gap. Runs while the marker is still
 * present, before the collapse passes.
 *
 * The following empty paragraph is asserted via lookahead (not consumed), so
 * each block is still evaluated as a potential predecessor on the next
 * iteration.
 */
const PARAGRAPH_BEFORE_EMPTY_RE = new RegExp(
  "(<(p|h[1-6])\\b[^>]*>(?:(?!<\\2\\b)[\\s\\S])*?</\\2>)" +
    "(?=\\s*<(p|h[1-6])\\b[^>]*>((?:(?!<\\3\\b)[\\s\\S])*?)</\\3>)",
  "gi",
);

function restoreGapBeforeCollapsedParagraph(html: string): string {
  return html.replace(
    PARAGRAPH_BEFORE_EMPTY_RE,
    (full, block: string, _blockTag: string, _nextTag: string, nextInner) => {
      const nextEmpty =
        typeof nextInner === "string" && isFieldOnlyEmptyParagraph(nextInner);
      if (!nextEmpty) return full;
      if (!ZERO_BOTTOM_MARGIN_RE.test(block)) return full;
      return restoreBottomMargin(block);
    },
  );
}

/**
 * Drop a table row when every cell would collapse away (typical centred
 * letterhead: one field per row). A row that still has another cell stays,
 * so a 2-column layout cannot shift.
 */
function dropEmptiedTableRows(html: string): string {
  return html.replace(ROW_RE, (full, _attrs: string, inner: string) => {
    if (!inner.includes(EMPTY_FIELD_MARKER)) return full;
    const cells = [...inner.matchAll(CELL_INNER_RE)];
    if (cells.length === 0) return full;
    const everyCellGoes = cells.every((m) => {
      const cellInner = m[3] ?? "";
      const kept = keptLinesAfterCollapse(cellInner);
      if (kept === null) return isVisuallyBlank(cellInner);
      return kept.length === 0;
    });
    return everyCellGoes ? "" : full;
  });
}

/**
 * Drop every line whose content came only from empty fields, then strip the
 * markers from the lines that survived.
 *
 * @param html - Rendered document HTML containing {@link EMPTY_FIELD_MARKER}.
 * @returns The same HTML with blank field-only lines removed.
 *
 * @example
 * // "41 Yarra Boulevard<br>Kew VIC 3101" — the empty line 2 is gone
 * collapseEmptyFieldLines(
 *   `<p>41 Yarra Boulevard<br>${EMPTY_FIELD_MARKER}<br>Kew VIC 3101</p>`,
 * );
 */
export function collapseEmptyFieldLines(html: string): string {
  if (!html.includes(EMPTY_FIELD_MARKER)) return html;

  // 0. Before anything is removed, hand back the bottom margin to any block
  //    that zeroed it only because a now-empty field paragraph directly below
  //    it was providing the gap — otherwise the collapse butts it against the
  //    following text (MOR: Trust ABN missing closed the heading/body gap).
  const withGapPreserved = restoreGapBeforeCollapsedParagraph(html);

  // 1. Table rows whose every cell is an empty field — drop the whole row
  //    while the marker is still present (a later <p> pass would otherwise
  //    empty the cell and leave a blank row we can no longer identify).
  // 2. Paragraphs / headings first so a wrapping <div> or <td> cannot
  //    swallow them as a single "line".
  const withParagraphsCollapsed = collapseMatchingBlocks(
    dropEmptiedTableRows(withGapPreserved),
    PARAGRAPH_RE,
    (c) => ({ tag: c[0] ?? "p", attrs: c[1] ?? "", inner: c[2] ?? "" }),
  );

  const withCellsCollapsed = collapseMatchingBlocks(
    withParagraphsCollapsed,
    CELL_RE,
    (c) => ({ tag: c[0] ?? "td", attrs: c[1] ?? "", inner: c[2] ?? "" }),
  );

  const collapsed = collapseMatchingBlocks(withCellsCollapsed, DIV_RE, (c) => ({
    tag: "div",
    attrs: c[0] ?? "",
    inner: c[1] ?? "",
  }));

  return collapsed.split(EMPTY_FIELD_MARKER).join("");
}
